import medicareApiAxios, { AxiosError as MedicareApiAxiosError } from '@cms-max/medicare-api-sdk/node_modules/axios/index'
import axios, { AxiosError } from 'axios'
import { ChatTokenRequest, ChatTokenResponse } from 'models/Chat'
import User from 'models/User'
import { SiteActivityRecord } from 'utils/sendCoreAPIEvent'
import { ISiteActivityApi, SiteActivityApiFactory, SubmissionType } from '@cms-max/medicare-api-sdk'
import { trimTrailingSlash } from 'utils/trim-trailing-slash'
import { getAppBaseUrl } from 'utils/getAppBaseUrl'
import { Logger } from 'utils/logger'

const coreUrl = process.env.REACT_APP_CORE_API_URL ?? getAppBaseUrl()

const siteActivityAxiosClient = medicareApiAxios.create({ withCredentials: true })
const siteActivity = SiteActivityApiFactory(undefined, trimTrailingSlash(coreUrl), siteActivityAxiosClient)

export type IPublicBeneficiaryResponse = Pick<User, 'firstName'>

/**
 * A utility function that will return false if the naviator send beacon is not a function.
 * This is so that we can easily fallback to other method for sending the payload.
 *
 * If the function exists, this will also return the response value from the navigator.sendBeacon call
 *
 * @param url url to send the request to
 * @param payload the object to pass to the url as json
 * @returns true if the navigator.sendBeacon function exists and is a function and if the request is queued successfully
 */
const sendBeaconHelper = (url: string, payload: any) => {
  if (typeof (navigator?.sendBeacon) !== 'function') {
    return false
  }

  const blob = new Blob(
    [JSON.stringify(payload)],
    {
      type: 'application/json'
    }
  )
  return navigator.sendBeacon(url, blob)
}

export const medicareApiClient = {
  async asyncGetBeneficiaryByMbi (mbi: string) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_CORE_API_URL}/beneficiaries`, { mbi }, { withCredentials: true })

      return response.data
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error getting data from Core API'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },
  async asyncGetToken () {
    try {
      const response = await axios.get(`${process.env.REACT_APP_CORE_API_URL}/authenticate`, { withCredentials: true })

      return response.data.temporaryToken
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error getting data from Core API'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },

  async asyncGetBeneficiaryByReqToken (slsxToken: string) {
    try {
      // TODO: add the post request back in after everything is working on the backend.
      // await axios.post(`${process.env.REACT_APP_CORE_API_URL}/authenticate`, { reqToken }, { withCredentials: true })
      const response = await axios.get<IPublicBeneficiaryResponse>(`${process.env.REACT_APP_CORE_API_URL}/beneficiary/${slsxToken}`, { withCredentials: true })
      return response.data
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error fetching beneficiary'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },

  async asyncGetBeneficiaryFullData () {
    try {
      const response = await axios.get<User>(`${process.env.REACT_APP_CORE_API_URL}/beneficiary`, { withCredentials: true })
      return response.data
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error getting data from Core API'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },

  async asyncGetMessages (languageCode: string) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_CORE_API_URL}/beneficiaries/messages`, {
        languageCode,
        templateType: 'web'
      }, { withCredentials: true })
      return response.data
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error fetching messages'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },
  async asyncMarkMessagesAsRead (payload: { messageIds: Array<string> }) { // change any to real type later
    try {
      const response = await axios.post(`${process.env.REACT_APP_CORE_API_URL}/beneficiaries/messages/markRead`, payload, { withCredentials: true })
      return response
    } catch (error) {
      let errorMessage = 'Error updating messages'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },
  async asyncGetToasts (languageCode: string) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_CORE_API_URL}/beneficiaries/messages`, {
        languageCode,
        templateType: 'toast'
      }, { withCredentials: true })
      return response.data
    } catch (error) {
      Logger.error(error)
      let errorMessage = 'Error fetching toasts'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },
  async asyncPostSiteActivity (payload: { siteActivity: SiteActivityRecord }) {
    if (sendBeaconHelper(`${process.env.REACT_APP_CORE_API_URL}/siteActivity`, payload)) {
      return
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_CORE_API_URL}/siteActivity`,
        payload,
        { withCredentials: true }
      )
      return response
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error getting data from Core API'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },
  async asyncPostSiteActivityV2 (activity: ISiteActivityApi) {
    const payload = {
      siteActivity: activity
    }
    if (sendBeaconHelper(`${process.env.REACT_APP_CORE_API_URL}/siteActivity`, payload)) {
      return
    }

    try {
      return await siteActivity.addSiteActivityRecord(payload)
    } catch (error) {
      Logger.error(error)
      let errorMessage = 'Error posting siteActivity to Core API'

      const axiosError = error as MedicareApiAxiosError
      if (axiosError.isAxiosError) {
        errorMessage = `HTTP status (${axiosError.response?.status}) - ${errorMessage}`
      }
      throw new Error(errorMessage)
    }
  },
  async asyncPostDownstreamEvent (activity: ISiteActivityApi, submissionType: SubmissionType) {
    const payload = {
      siteActivity: activity,
      submissionType
    }
    if (sendBeaconHelper(`${process.env.REACT_APP_CORE_API_URL}/downstreamEvent`, payload)) {
      return
    }

    try {
      return await siteActivity.addDownstreamEvent(payload)
    } catch (error) {
      Logger.error(error)
      let errorMessage = 'Error posting siteActivity downstream event to Core API'

      const axiosError = error as MedicareApiAxiosError
      if (axiosError.isAxiosError) {
        errorMessage = `HTTP status (${axiosError.response?.status}) - ${errorMessage}`
      }
      throw new Error(errorMessage)
    }
  },
  async asyncEncryptChatInfo (payload: ChatTokenRequest) {
    try {
      const response = await axios.post<ChatTokenResponse>(`${process.env.REACT_APP_CORE_API_URL}/chat`, payload, { withCredentials: true })
      return response.data
    } catch (error) {
      console.error(error)
      let errorMessage = 'Error getting data from Core API'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  }
}
